import HelpIcon from '@mui/icons-material/HelpOutline';
import { Popover } from '@mui/material';
import VanillaButton, { ButtonProps as VanillaButtonProps } from '@mui/material/Button';
import MuiCard, { CardProps as MuiCardProps } from '@mui/material/Card';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import VanillaChip, { ChipProps } from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectProps } from '@mui/material/Select';
import MuiSwitch from '@mui/material/Switch';
import MuiTextField, { OutlinedTextFieldProps as MUIOutlinedTextFieldProps } from '@mui/material/TextField';
import camelize from 'camelize';
import fontFns from 'fontFns';
import useThemedColor from 'hooks/useThemedColor';
import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import { EColors, TColorStyledFn, colorStyledFn } from 'theme';
import CloseIcon from './images/icons/close.svg?react';
import DeleteIcon from './images/icons/trash-can.svg?react';
import { copyFontSizes, withIconStyles } from './shared';
import { getFirstMatchingKey, matchLastOrDefault } from './util';

export const MAX_WIDTH = 1350;

export const SPACER_SPACES = {
    xsmall: 6,
    small: 12,
    smallish: 20,
    default: 24,
    medium: 32,
    large: 36,
    larger: 48,
    largest: 60,
} as const;
export type TSpacerNames = keyof typeof SPACER_SPACES;

type TItemSpacingProps = {
    itemSpacing?: TSpacerNames;
    paddingSpacing?: boolean;
    // meant to be used with itemSpacing to allow proper borders
    withBorderPadding?: TSpacerNames;
};

type TColProps = {
    scrollable?: boolean;
    alignItems?: React.CSSProperties['alignItems'];
    justifyContent?: React.CSSProperties['justifyContent'];
    fillWidth?: boolean;
    wrap?: boolean;
} & TItemSpacingProps;

export const ColBase = styled.div.withConfig({
    shouldForwardProp: prop =>
        ![
            'scrollable',
            'alignItems',
            'justifyContent',
            'fillWidth',
            'wrap',
            'itemSpacing',
            'paddingSpacing',
            'withBorderPadding',
        ].includes(prop),
})<TColProps>`
    display: flex;
    flex-direction: column;
    ${props => props.scrollable && 'overflow: auto;'}
    ${props => props.alignItems && `align-items: ${props.alignItems};`}
    ${props => props.justifyContent && `justify-content: ${props.justifyContent};`}
    ${props => props.fillWidth && `width: 100%;`}
    ${props => (props.wrap ? 'flex-wrap: wrap;' : '')};
`;

type RowProps = {
    alignItems?: React.CSSProperties['alignItems'];
    justifyContent?: React.CSSProperties['justifyContent'];
    wrap?: boolean;
} & TItemSpacingProps;

export const RowBase = styled.div.withConfig({
    shouldForwardProp: prop =>
        !['alignItems', 'justifyContent', 'wrap', 'itemSpacing', 'paddingSpacing', 'withBorderPadding'].includes(prop),
})<RowProps>`
    display: flex;
    width: 100%;
    align-items: ${props => (props.alignItems ? props.alignItems : 'flex-start')};
    justify-content: ${props => (props.justifyContent ? props.justifyContent : 'flex-start')};
    ${props => (props.wrap ? 'flex-wrap: wrap;' : '')};
`;

export const Column = styled(ColBase).withConfig({
    shouldForwardProp: prop => !['itemSpacing', 'withBorderPadding'].includes(prop),
})<TColProps>`
    ${({ itemSpacing }) => (itemSpacing ? `gap: ${SPACER_SPACES[itemSpacing]}px;` : '')}

    ${({ withBorderPadding, itemSpacing }) =>
        withBorderPadding
            ? `
    margin-top: 0px;
    margin-bottom: 0px;

    padding-top: ${SPACER_SPACES[withBorderPadding] - (itemSpacing ? SPACER_SPACES[itemSpacing] / 2 : 0)}px;
    padding-bottom: ${SPACER_SPACES[withBorderPadding] - (itemSpacing ? SPACER_SPACES[itemSpacing] / 2 : 0)}px;
            `
            : ``}
`;

export const AlignedColumn = styled(Column)`
    align-items: center;
`;

export const SpacedColumn = styled(Column)`
    justify-content: space-between;
`;

export const Row = styled(RowBase).withConfig({
    shouldForwardProp: prop => !['itemSpacing', 'withBorderPadding'].includes(prop),
})<RowProps>`
    ${({ itemSpacing }) => (itemSpacing ? `gap: ${SPACER_SPACES[itemSpacing]}px;` : '')}

    ${({ withBorderPadding, itemSpacing }) =>
        withBorderPadding
            ? `
    margin-left: 0px;
    margin-right: 0px;

    padding-left: ${SPACER_SPACES[withBorderPadding] - (itemSpacing ? SPACER_SPACES[itemSpacing] / 2 : 0)}px;
    padding-right: ${SPACER_SPACES[withBorderPadding] - (itemSpacing ? SPACER_SPACES[itemSpacing] / 2 : 0)}px;
            `
            : ``}
`;

export const AlignedRow = styled(Row)`
    align-items: center;
`;

export const InlineRow = styled(Row)`
    width: auto;
`;

export const SpacedRow = styled(AlignedRow)`
    justify-content: space-between;
`;

export const ShadedRow = styled(SpacedRow)`
    width: unset;
    background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.softAccentedBackground)};
    padding: 12px 16px;
`;

type TopRoundedProps = {
    background: Themed.Color;
    hasContentBelow: boolean;
    backgroundHover: Themed.Color;
};

export const TopRounded = styled(ShadedRow).withConfig({
    shouldForwardProp: prop => !['background', 'hasContentBelow', 'backgroundHover'].includes(prop),
})<Partial<TopRoundedProps>>`
    background-color: ${({ background, theme: { getColor, EColors } }) =>
        background || getColor(EColors.paneListItemBackground)};
    border-radius: ${props => (props.hasContentBelow ? '12px 12px 0 0' : '12px')};
    border-bottom: ${({ hasContentBelow, theme: { getColor, EColors } }) =>
        hasContentBelow ? `1px solid ${getColor(EColors.softBorder)}` : 'none'};

    ${props =>
        props.backgroundHover &&
        `
            &:hover {
                background-color: ${props.backgroundHover};
            }
        `}
`;

export const ExpandedPane = styled(Column).withConfig({
    shouldForwardProp: prop => !['constrain'].includes(prop),
})<{ constrain?: boolean }>`
    max-height: ${props => (props.constrain ? '330px' : 'unset')};
    overflow: ${props => (props.constrain ? 'scroll' : 'unset')};
    border-radius: 0 0 12px 12px;
`;

export const MaxWidthRow = styled(Row)`
    max-width: ${MAX_WIDTH}px;
    align-items: center;
    align-self: center;
`;

export const Line = styled.div.withConfig({
    shouldForwardProp: prop => !['fullWidth', 'weight'].includes(prop),
})<{ fullWidth?: boolean; weight?: number }>`
    height: ${props => (props.weight ? props.weight : 1)}px;
    background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.lightGrey)};
    ${props => props.fullWidth && 'width: 100%;'}
`;

export const Grid = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
`;

type TSpacesProps = Partial<{ col?: boolean } & Record<TSpacerNames, boolean>>;

export const Spacer = styled.div.withConfig({
    shouldForwardProp: prop =>
        !['col', 'xsmall', 'small', 'smallish', 'default', 'medium', 'large', 'larger', 'largest'].includes(prop),
})<TSpacesProps>`
    ${(props: TSpacesProps) =>
        props.col
            ? `    
        height: 24px;
        width: ${matchLastOrDefault(SPACER_SPACES, props)}px;
        min-width: ${matchLastOrDefault(SPACER_SPACES, props)}px;
    `
            : `
        width: 24px;
        height: ${matchLastOrDefault(SPACER_SPACES, props)}px;
        min-height: ${matchLastOrDefault(SPACER_SPACES, props)}px;
    `}
`;

export const Card = styled.div.withConfig({
    shouldForwardProp: prop => !['scrollable'].includes(prop),
})<{ scrollable?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px 24px 24px 24px;
    border-radius: 12px;
    margin-right: 24px;
    margin-bottom: 24px;
    background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.dashboardCard)};
    ${props => props.scrollable && 'overflow: auto;'}
`;

type CopyProps = {
    maxWidth?: string | number;
    faded?: boolean;
    htmlFor?: string;
    xlarge?: boolean;
    large?: boolean;
    small?: boolean;
    xsmall?: boolean;
    regular?: boolean;
    children?: React.ReactNode;
} & TColorStyledFn;

export const Copy = styled.div.withConfig({
    shouldForwardProp: prop =>
        !['maxWidth', 'faded', 'xlarge', 'large', 'small', 'xsmall', 'regular', '$color'].includes(prop),
})<Partial<CopyProps>>`
    ${({ $color, color }) => (!($color || color) ? 'color: inherit' : color ? `color: ${color}` : '')};
    ${colorStyledFn};
    font-size: ${props =>
        getFirstMatchingKey(props, copyFontSizes)
            ? copyFontSizes[getFirstMatchingKey(props, copyFontSizes)]
            : copyFontSizes.regular};
    line-height: 20px;
    max-width: ${props =>
        props.maxWidth ? (props.maxWidth.toString().includes('%') ? props.maxWidth : `${props.maxWidth}px`) : 'unset'};
    ${props => props.faded && 'opacity: 0.5;'}
`;

export const CopyFaded = (props: Partial<CopyProps>) => <Copy faded {...props} />;
export const SmallerCopy = (props: Partial<CopyProps>) => <Copy small {...props} />;

export const TruncatingSingleLineCopy = styled(Copy)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const LowercaseCopy = styled(Copy)`
    text-transform: lowercase;
`;

export const PreCopy = styled(Copy)`
    white-space: pre-line;
`;

export const RichTextCopy = styled(Copy)`
    a {
        color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryAction)};
    }
`;

export const Link = styled.a.withConfig({
    shouldForwardProp: prop => !['openInNewTab', 'underline'].includes(prop),
})<{ openInNewTab?: boolean; underline?: boolean }>`
    color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryAction)};
    &:hover,
    &:focus {
        color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryActionHover)};
    }
    cursor: pointer;
    ${({ underline }) => (underline ? `text-decoration: underline;` : '')}
`;

export const ExternalLink = styled(Link)
    .withConfig({
        shouldForwardProp: prop => !['openInNewTab'].includes(prop),
    })
    .attrs(({ openInNewTab }) => ({
        target: openInNewTab ? '_blank' : undefined,
        rel: openInNewTab ? 'noopener noreferrer external' : undefined,
    }))``;

const FlatVanillaButton = styled(VanillaButton)`
    border-radius: 3px;

    box-shadow: none;

    &:hover,
    &:active {
        box-shadow: none;
        ${({ variant, theme: { getColor, EColors } }) =>
            variant === 'contained' && `background-color: ${getColor(EColors.primaryActionHover)};`}
    }

    ${({ variant, theme: { getColor, EColors } }) =>
        variant === 'outlined'
            ? {
                  backgroundColor: getColor(EColors.pureWhite),
                  borderColor: getColor(EColors.primaryAction),
                  '&:hover, &:focus': {
                      backgroundColor: getColor(EColors.primaryAction),
                      '.MuiButton-label': {
                          color: getColor(EColors.pureWhite),
                      },
                  },
              }
            : {}}

    .MuiButton-label {
        white-space: nowrap;
    }
`;

export const Button = ({
    children,
    style,
    ...props
}: {
    style?: React.CSSProperties;
    children: React.ReactNode;
} & Omit<VanillaButtonProps, 'style' | 'children'>) => (
    <FlatVanillaButton variant="contained" color="primary" style={style} {...props}>
        {children}
    </FlatVanillaButton>
);

const whiteButtonStyles = (themedColors: ReturnType<typeof useThemedColor>) => ({
    display: 'inline-block',
    backgroundColor: `${themedColors.pureWhite} !important`,
    color: themedColors.primaryAction,
    whiteSpace: 'nowrap',
});

export const WhiteActionButton = ({ children, ...props }: VanillaButtonProps) => {
    const themedColors = useThemedColor();
    return (
        <Button style={{ ...whiteButtonStyles(themedColors), marginRight: 16 }} variant="outlined" {...props}>
            {children}
        </Button>
    );
};

export const CustomChip = (props: ChipProps) => (
    <VanillaChip color="primary" {...props} style={{ height: '20px', ...props.style }} />
);

export const StatusChip = styled(VanillaChip).withConfig({
    shouldForwardProp: prop => !['statusColor'].includes(prop),
})<{ statusColor: EColors }>`
    &.MuiChip-root {
        color: ${({ theme: { getColor, EColors } }) => getColor(EColors.pureWhite)};
        background-color: ${({ statusColor, theme: { getColor } }) => getColor(statusColor)};
        height: 29px;
        text-transform: uppercase;
        border-radius: 4px;
        text-transform: capitalize;
    }
`;

export const PaddedCard = styled(MuiCard).withConfig({
    shouldForwardProp: prop => !['padding'].includes(prop),
})<MuiCardProps & { padding?: number }>`
    padding: ${props => (props.padding || props.padding === 0 ? props.padding : 32)}px;

    border-radius: 8px;
    box-shadow: 0 2px 44px 0 ${({ theme: { getColor, EColors } }) => getColor(EColors.pureBlack, 0.1)};
`;

export const Matchbox = styled(Column).withConfig({
    shouldForwardProp: prop => !['flat', 'selected', 'onClick'].includes(prop),
})<{ flat?: boolean; selected?: boolean; onClick?: () => void }>`
    ${({ selected, theme: { getColor, EColors } }) =>
        selected &&
        `
    border-radius: 4px 4px;
    box-shadow: 0 4px 12px 0 ${getColor(EColors.pureBlack, 0.05)},
                0 6px 20px 0 ${getColor(EColors.pureBlack, 0.05)};
              `}

    ${({ flat, theme: { getColor, EColors } }) =>
        flat
            ? ''
            : `
          &:hover {
            border-radius: 12px 12px;
            box-shadow: 0 4px 12px 0 ${getColor(EColors.pureBlack, 0.05)},
                        0 6px 20px 0 ${getColor(EColors.pureBlack, 0.05)};
          }
        `}

    ${({ onClick }) => onClick && 'cursor: pointer;'}
`;

type LabeledCheckboxProps = {
    disabled: boolean;
    isChecked: boolean;
    label: React.ReactNode;
    onChange: (evt: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    className?: string;
    customIcon?: React.ReactSVGElement;
    customIconChecked?: React.ReactSVGElement;
} & CheckboxProps;

export const LabeledCheckbox = ({
    disabled = false,
    isChecked = false,
    label = '',
    onChange,
    className = '',
    customIcon,
    customIconChecked,
    ...rest
}: LabeledCheckboxProps) => (
    <FormControlLabel
        className={className}
        label={label}
        control={
            <Checkbox
                color="primary"
                disabled={disabled}
                checked={isChecked}
                onChange={onChange}
                icon={customIcon}
                checkedIcon={customIconChecked}
                {...rest}
            />
        }
    />
);

type TextFieldProps = {
    rowStyle?: object;
    charLimit?: number;
};

export const TextField = ({
    children,
    rowStyle,
    className,
    charLimit,
    ...props
}: Partial<TextFieldProps & MUIOutlinedTextFieldProps>) => {
    const { pureWhite } = useThemedColor();
    return (
        <AlignedRow style={rowStyle} className={className}>
            <MuiTextField
                margin="dense"
                style={{
                    backgroundColor: pureWhite,
                }}
                variant="outlined"
                inputProps={{ ...(charLimit ? { maxLength: charLimit } : {}) }}
                {...props}
            />
            {children}
        </AlignedRow>
    );
};

export const TextArea = (props: Partial<TextFieldProps & MUIOutlinedTextFieldProps>) => (
    <TextField multiline rows={4} maxRows={8} fullWidth={true} {...props} />
);

export const PromptedTextField = ({
    children,
    prompt,
    optional,
    onDelete,
    ...props
}: MUIOutlinedTextFieldProps & {
    prompt?: string;
    charLimit?: number;
    optional?: boolean;
    onDelete?: () => void;
}) => (
    <Prompted prompt={prompt} optional={optional} onDelete={onDelete}>
        <TextField id={prompt} fullWidth={true} {...props} />
        {children}
    </Prompted>
);

const DeleteIconWithStyles = withIconStyles(DeleteIcon);

const FormLabel = styled(Copy)`
    color: ${({ theme: { getColor, EColors } }) => getColor(EColors.formLabel)};
    ${fontFns.formLabel}
`;

export const Prompted = ({
    children,
    prompt,
    onDelete,
    optional,
}: {
    children: React.ReactNode;
    prompt?: string;
    optional?: boolean;
    onDelete?: () => void;
}) => {
    const themedColors = useThemedColor();

    return (
        <Column style={{ marginBottom: !optional ? 16 : 0 }}>
            <SpacedRow style={{ minHeight: 30 }}>
                {prompt && (
                    <FormLabel as="label" htmlFor={prompt}>
                        {prompt}{' '}
                        {!optional && (
                            <LowercaseCopy as="span" color={themedColors.optionalSpecifier}>
                                (Required)
                            </LowercaseCopy>
                        )}
                    </FormLabel>
                )}
                {onDelete && (
                    <DeleteIconWithStyles
                        style={{
                            marginLeft: '16px',
                        }}
                        onClick={onDelete}
                    />
                )}
            </SpacedRow>
            {children}
        </Column>
    );
};

export const LabeledDropdown = ({ label, width = 120, ...props }: { label: string; width: number }) => (
    <FormControl variant="outlined" style={{ margin: '8px 0', width: `${width}px` }}>
        <InputLabel>{label}</InputLabel>
        <Dropdown width={width} label={label} {...props} />
    </FormControl>
);

type DropdownProps = {
    label: string;
    width: number;
};

export const Dropdown = ({
    children,
    label,
    onChange,
    value,
    className,
}: Omit<SelectProps, 'label'> & DropdownProps) => (
    <Select
        value={value}
        onChange={onChange}
        className={className}
        input={<OutlinedInput margin="dense" label={label} name={camelize(label)} id={camelize(label)} />}
    >
        {children}
    </Select>
);

export const PlaceholderIcon = ({ color, style }: Partial<{ color: Themed.Color; style: object }>) => {
    const { darkestGrey } = useThemedColor();
    return <HelpIcon style={{ ...style, color: color || darkestGrey, fontSize: '24px' }} />;
};

export const AccentedPlaceholderIcon = styled(PlaceholderIcon)`
    color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryDisplayIcon)};
`;

export const FixedBackground = styled.div<{ backgroundColor: string }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: -1;

    ${props => (props.backgroundColor ? 'background-color: ' + props.backgroundColor : '')};
`;

const CloseIconTopLeft = styled.a`
    display: flex;

    position: fixed;
    top: 44px;
    right: 44px;

    color: ${({ theme: { getColor, EColors } }) => getColor(EColors.darkerGrey)};
    cursor: pointer;
`;

export const BackgroundWithClose = ({ backgroundColor, onClose }: { backgroundColor: string; onClose: () => void }) => (
    <div>
        <FixedBackground backgroundColor={backgroundColor} />
        <CloseIconTopLeft>
            <CloseIcon onClick={onClose} />
        </CloseIconTopLeft>
    </div>
);

export const Switch = styled(MuiSwitch)`
    &.MuiSwitch-root {
        width: 2.5rem;
        height: 1.25rem;
        padding: 0;
        border-radius: 0.625rem;
        border-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.pureWhite)};

        &.MuiSwitch-sizeMedium {
            background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryAction)};
            border-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.pureWhite)};
        }

        .MuiSwitch-switchBase {
            background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.imagePlaceholderIcon)};
            color: ${({ theme: { getColor, EColors } }) => getColor(EColors.imagePlaceholderIcon)};
            padding: 0;

            &.Mui-checked {
                background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryAction)};
                color: ${({ theme: { getColor, EColors } }) => getColor(EColors.pureWhite)};
                transform: translateX(19px);

                + .MuiSwitch-track {
                    opacity: 1;
                    border: 0;
                    background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryAction)};
                    border-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.primaryAction)};
                }
            }
        }
    }

    & .MuiSwitch-thumb {
        color: ${({ theme: { getColor, EColors } }) => getColor(EColors.pureWhite)};
        box-sizing: border-box;
        height: 1rem;
        width: 1rem;
        margin: 0.125rem;
    }

    & .MuiSwitch-track {
        background-color: ${({ theme: { getColor, EColors } }) => getColor(EColors.darkGrey)};
        opacity: 1;
        border-radius: 0px;
    }
`;

export const Strike = styled.s`
    text-decoration-thickness: from-font;
    text-decoration-style: double;
`;

export const StyledPopover = styled(Popover)`
    pointer-events: none;
`;

export const PopoverContainer = styled.div`
    padding: 10px;
`;
